import React, { useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import UndoIcon from '@mui/icons-material/Undo'
import API from '../../../../services/API'
import { vatMultiplierForItem } from '../../../../helpers/region'

// Full credits the invoice as it stands (minus prior credits); part lets
// the user pick which lines/quantities to credit back. Mirrors
// pages/patientTransport/Invoices/PTSInvoices.jsx's embedded
// CreditNoteDialog, kept separate (rather than shared) so PTS's proven flow
// is never touched — this one posts to the generic finance endpoint
// instead of the PTS-specific one, and is region/reduced-VAT aware.
const getLineItemsTotal = (items = [], region) =>
  items.reduce((total, item) => {
    const multiplier = vatMultiplierForItem(item.vat, region)
    return total + (Number(item.quantity) || 0) * (Number(item.unitPrice) || 0) * multiplier
  }, 0)

const getInvoiceCredited = (invoice, region) =>
  (invoice?.creditNotes || []).reduce(
    (total, note) => total + getLineItemsTotal(note.items, region),
    0,
  )

const CreditNoteDialog = ({ invoice, currency, region, onClose, onIssued }) => {
  const [type, setType] = useState('full')
  const [items, setItems] = useState([])
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!invoice) return
    setType('full')
    setItems((invoice.items || []).map((item) => ({ ...item })))
    setReason('')
    setError('')
  }, [invoice])

  if (!invoice) return null

  const alreadyCredited = getInvoiceCredited(invoice, region)
  const invoiceTotal = getLineItemsTotal(invoice.items, region)
  // Net of any flat discount, matching the server's cap - crediting the
  // gross of a discounted invoice would leave it owing a negative amount
  const remainingCreditable = Math.max(
    0,
    invoiceTotal - alreadyCredited - (Number(invoice.discount) || 0),
  )
  const creditTotal =
    type === 'full' ? remainingCreditable : getLineItemsTotal(items, region)

  const setLine = (index, field, value) =>
    setItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)),
    )

  const submit = async () => {
    setBusy(true)
    setError('')
    try {
      const res = await API.post(`/finance/invoice/${invoice._id}/credit-notes`, {
        type,
        items: type === 'part' ? items : undefined,
        reason,
      })
      if (res.status === 201) {
        onIssued?.()
        onClose?.()
      } else {
        setError(res?.data?.error || 'Unable to issue credit note.')
      }
    } catch (e) {
      setError('Unable to issue credit note. Please try again later.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={!!invoice} onClose={busy ? null : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Credit note — {invoice.reference}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} pt={1}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            {currency}
            {remainingCreditable.toFixed(2)} left to credit on this invoice
            {alreadyCredited > 0
              ? ` (${currency}${alreadyCredited.toFixed(2)} already credited).`
              : '.'}
          </Typography>
          <RadioGroup row value={type} onChange={(e) => setType(e.target.value)}>
            <FormControlLabel value="full" control={<Radio />} label="Full credit" />
            <FormControlLabel value="part" control={<Radio />} label="Partial credit" />
          </RadioGroup>
          {type === 'part' && (
            <Stack spacing={1}>
              {items.map((item, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    value={item.description}
                    onChange={(e) => setLine(index, 'description', e.target.value)}
                  />
                  <TextField
                    label="Qty"
                    type="number"
                    size="small"
                    value={item.quantity}
                    onChange={(e) => setLine(index, 'quantity', e.target.value)}
                    sx={{ width: 90 }}
                  />
                  <TextField
                    label={`Unit (${currency})`}
                    type="number"
                    size="small"
                    value={item.unitPrice}
                    onChange={(e) => setLine(index, 'unitPrice', e.target.value)}
                    sx={{ width: 120 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  setItems((prev) => [
                    ...prev,
                    { description: '', quantity: 1, unitPrice: 0, vat: 'none' },
                  ])
                }
              >
                Add line
              </Button>
            </Stack>
          )}
          <TextField
            label="Reason"
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <Divider />
          <Typography sx={{ fontWeight: 800 }}>
            Credit total: {currency}
            {creditTotal.toFixed(2)}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={<UndoIcon />}
          disabled={busy || creditTotal <= 0}
          onClick={submit}
        >
          {busy ? 'Issuing...' : 'Issue Credit Note'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreditNoteDialog
